/* eslint-disable max-len */
import React, { FC, useState } from 'react'
import { RoleMember, RoleTeam, SettingCustomAlert } from '../../../../states/types/setting'
import Tips from '../../common/Tips'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ATTENDANCE_CONDITION_TYPE, CONDITION_TYPE, OPERATOR_TYPE, TIMING_TYPE, TIMING_VALUES } from '../../../constants/customAlert'
import { secToHour, secToMinute, standardToAttendanceTime } from '../../../../utils/date'

export interface CustomAlertConditions {
  condition_type: string
  operator_type: string
  time?: string
  seconds?: number
  biz_point?: number
  rate?: number
  service?: {
    id: number
    name: string
  }
}

interface Props {
  customAlert: SettingCustomAlert
  openCustomAlertModalCallback: (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>) => void
  duplicateCustomAlertCallback: (id: number) => void
  openConfirmationModalCallback: (type: string, id: number) => void
  changeAlertStatusCallback: (id: number, active: boolean) => void
  teamList: RoleTeam[]
}

const SCOPE_LABEL: Record<string, string> = {
  both: "マネージャー・メンバー",
  manager: "マネージャー",
  member: "メンバー",
}

const CustomAlertDetail: FC<Props> = (props) => {
  const {
    customAlert,
    openCustomAlertModalCallback,
    duplicateCustomAlertCallback,
    openConfirmationModalCallback,
    changeAlertStatusCallback,
    teamList,
  } = props

  const [isOpenDetail, setIsOpenDetail] = useState<boolean>(false)

  const getAllMemberList = (id: number | null): RoleMember[] => {
    const tmpTeams = teamList.filter((v) => v.parent_team_id === id)
    if (!tmpTeams.length) return []
    const tmpMembers = [...tmpTeams.map((v) => [...v.managers, ...v.members]).flat(), ...tmpTeams.map((v) => getAllMemberList(v.id).flat()).flat()]
    return Array.from(new Map(tmpMembers.map((v) => [v.id, v])).values())
  }

  const buildAlertCondition = (condition: CustomAlertConditions) => {
    let value: string | null = ""
    let unit = ""
    let value_2: string | null = ""
    let unit_2 = ""
    if (
      condition.condition_type === "biz_point"
      || condition.condition_type === "biz_point_hour"
      || condition.condition_type === "application_biz_point"
    ) {
      value = condition.biz_point?.toString() || "0"
      unit = "biz"
    } else if (
      condition.condition_type === "daily_work_time"
      || condition.condition_type === "weekly_work_time"
      || condition.condition_type === "monthly_work_time"
      || condition.condition_type === "daily_over_time"
      || condition.condition_type === "weekly_over_time"
      || condition.condition_type === "monthly_over_time"
      || condition.condition_type === "holiday_work_time"
      || condition.condition_type === "holiday_active_time"
    ) {
      value = secToHour(condition.seconds || 0).toString()
      unit = "時間"
    } else if (
      condition.condition_type === "active_time"
      || condition.condition_type === "not_active_time"
      || condition.condition_type === "application_time"
    ) {
      value = secToHour(condition.seconds || 0).toString()
      unit = "時間"
      value_2 = secToMinute(condition.seconds || 0).toString()
      unit_2 = "分"
    } else if (
      condition.condition_type === "labeling_rate"
    ) {
      value = condition.rate?.toString() || "0"
      unit = "%"
    } else {
      const timeList = condition.time?.split(":") || ["0", "0"]
      value = standardToAttendanceTime(Number(timeList[0]) ?? 0, Number(timeList[1]) ?? 0)
      unit = ""
    }
    return (
      <>
        <span
          style={{
            fontSize: "18px",
          }}
        >
          {value}
        </span>
        {unit}
        {
          !!value_2 && value_2 !== "0" ? (
            <>
              <span
                style={{
                  fontSize: "18px",
                }}
              >
                {value_2}
              </span>
              {unit_2}
            </>
          ) : (
            <></>
          )
        }
      </>
    )
  }

  const buildMemberChip = (member: RoleMember) => (
    <div
      style={{
        border: "solid 1px #CCCCCC",
        borderRadius: "20px",
        padding: "4px 8px",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        userSelect: "none",
        backgroundColor: "#FFFFFF",
      }}
      key={`chip_${member.id}`}
    >
      <img
        alt="profile"
        src={
          member.profile_image ?? `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`
        }
        className="profile_icon"
        style={{
          borderRadius: "50%",
          width: "14px",
        }}
      />
      {member.name}
    </div>
  )

  return (
    <>
      <div
        style={{
          position: "relative",
          border: `solid 1px ${customAlert.active ? "#999999" : "#CCCCCC"}`,
          borderRadius: "4px",
          padding: "8px",
          backgroundColor: customAlert.active ? "#FFFFFF" : "#EEEEEE",
          color: customAlert.active ? "#333333" : "#999999",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "46px",
            zIndex: 1,
          }}
          onClick={() => setIsOpenDetail(!isOpenDetail)}
          role="none"
        />
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: "8px",
            padding: "4px 8px",
            borderBottom: isOpenDetail ? `solid 1px ${customAlert.active ? "#666666" : "#CCCCCC"}` : "none",
            marginBottom: isOpenDetail ? "8px" : "0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              zIndex: 2,
              minWidth: "170px",
            }}
          >
            <div
              key={`${customAlert.id}_${customAlert.active}`}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "4px",
                fontSize: "10px",
                minWidth: 0,
              }}
            >
              <Tips
                description={customAlert.active ? "このアラートは有効になっています" : "このアラートは無効になっています"}
                className=""
                icon={false}
              >
                <div
                  style={{
                    color: customAlert.active ? "#0D9554" : "#FF0000",
                    fontSize: "16px",
                  }}
                >
                  <FontAwesomeIcon icon={['fas', customAlert.active ? 'bell' : 'bell-slash']} fixedWidth />
                </div>
              </Tips>
              <div style={{ flexShrink: 0 }}>
                アラート名
              </div>
              <Tips
                description={customAlert.name}
                className=""
                icon={false}
                style={{
                  minWidth: 0,
                }}
              >
                <>
                  <div
                    data-type="update"
                    data-id={customAlert.id}
                    onClick={(event) => customAlert.active && openCustomAlertModalCallback(event)}
                    style={{
                      fontSize: "18px",
                      color: customAlert.active ? "#4472BA" : "#999999",
                      cursor: customAlert.active ? "pointer" : "default",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      minWidth: 0,
                      whiteSpace: "nowrap",
                    }}
                    role="none"
                  >
                    {customAlert.name}
                  </div>
                </>
              </Tips>
            </div>
            <button
              type="button"
              onClick={() => setIsOpenDetail(!isOpenDetail)}
              style={{
                border: "solid 1px #999999",
                borderRadius: "4px",
                padding: "4px",
                fontSize: "10px",
                color: "#666666",
                backgroundColor: "#FFFFFF",
                display: "flex",
                flexShrink: 0,
                alignItems: "center",
                gap: "2px",
              }}
            >
              {isOpenDetail ? "閉じる" : "開く"}
            </button>
          </div>
          <div
            style={{
              display: "flex",
              flexShrink: 0,
              alignItems: "center",
              justifyContent: "right",
              gap: "8px",
            }}
          >
            {
              !isOpenDetail && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "4px",
                      fontSize: "10px",
                    }}
                  >
                    <div>
                      アラート通知人数
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "baseline",
                      }}
                    >
                      <div>
                        {customAlert.recipient.member_count}
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                        }}
                      >
                        人
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "4px",
                      fontSize: "10px",
                    }}
                  >
                    <div>
                      通知タイミング
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        display: "flex",
                      }}
                    >
                      <div>
                        {TIMING_TYPE.find((v) => v.value === customAlert.send_timing)?.label}
                      </div>
                      {
                        customAlert.send_timing !== TIMING_VALUES.IMMEDIATE && (
                          <div>
                            {customAlert.send_time}
                          </div>
                        )
                      }
                    </div>
                  </div>
                </>
              )
            }
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "4px",
                fontSize: "10px",
              }}
            >
              <div>
                現在の状況
              </div>
              <div
                style={{
                  fontSize: "16px",
                  color: customAlert.active ? "#0D9554" : "#FF0000",
                }}
              >
                {customAlert.active ? "有効" : "無効"}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "4px",
                zIndex: 2,
              }}
            >
              <Tips
                description="アラートの複製"
                className=""
                icon={false}
              >
                <>
                  <button
                    type="button"
                    onClick={() => duplicateCustomAlertCallback(customAlert.id)}
                    style={{
                      border: "solid 1px #999999",
                      borderRadius: "4px",
                      padding: "4px",
                      fontSize: "10px",
                      color: "#666666",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <FontAwesomeIcon icon={['far', 'clone']} fixedWidth />
                    複製
                  </button>
                </>
              </Tips>
              {
                customAlert.active ? (
                  <>
                    <Tips
                      description="アラートの編集"
                      className=""
                      icon={false}
                    >
                      <>
                        <button
                          type="button"
                          data-type="update"
                          data-id={customAlert.id}
                          onClick={(event) => openCustomAlertModalCallback(event)}
                          style={{
                            border: "solid 1px #999999",
                            borderRadius: "4px",
                            padding: "4px",
                            fontSize: "10px",
                            color: "#666666",
                            backgroundColor: "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <FontAwesomeIcon icon={['fas', 'cog']} fixedWidth />
                          編集
                        </button>
                      </>
                    </Tips>
                    <Tips
                      description="アラートの停止"
                      className=""
                      icon={false}
                    >
                      <>
                        <button
                          type="button"
                          onClick={() => openConfirmationModalCallback("stop", customAlert.id)}
                          style={{
                            border: "solid 1px #999999",
                            borderRadius: "4px",
                            padding: "4px",
                            fontSize: "10px",
                            color: "#FF0000",
                            backgroundColor: "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <FontAwesomeIcon icon={['fas', 'bell-slash']} fixedWidth />
                          停止
                        </button>
                      </>
                    </Tips>
                  </>
                ) : (
                  <Tips
                    description="アラートの開始"
                    className=""
                    icon={false}
                  >
                    <>
                      <button
                        type="button"
                        onClick={() => changeAlertStatusCallback(customAlert.id, true)}
                        style={{
                          border: "solid 1px #999999",
                          borderRadius: "4px",
                          padding: "4px",
                          fontSize: "10px",
                          color: "#0D9554",
                          backgroundColor: "#FFFFFF",
                          display: "flex",
                          alignItems: "center",
                          gap: "2px",
                        }}
                      >
                        <FontAwesomeIcon icon={['fas', 'bell']} fixedWidth />
                        開始
                      </button>
                    </>
                  </Tips>
                )
              }
              <Tips
                description="アラートの削除"
                className=""
                icon={false}
              >
                <>
                  <button
                    type="button"
                    onClick={() => openConfirmationModalCallback("delete", customAlert.id)}
                    style={{
                      border: "solid 1px #999999",
                      borderRadius: "4px",
                      padding: "4px",
                      fontSize: "10px",
                      color: "#FF0000",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <FontAwesomeIcon icon={['fas', 'trash-alt']} fixedWidth />
                    削除する
                  </button>
                </>
              </Tips>
            </div>
          </div>
        </div>
        {
          isOpenDetail && (
            <>
              <div
                style={{
                  padding: "8px",
                }}
              >
                <div
                  style={{
                    padding: "0 8px",
                    marginBottom: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: customAlert.active ? "#666666" : "#999999",
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'tag']} fixedWidth />
                  アラート名
                </div>
                <div
                  style={{
                    padding: "8px",
                    backgroundColor: "#F7F7F7",
                    borderRadius: "4px",
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  {customAlert.name}
                </div>
              </div>
              <div
                style={{
                  padding: "8px",
                }}
              >
                <div
                  style={{
                    padding: "0 8px",
                    marginBottom: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: customAlert.active ? "#666666" : "#999999",
                  }}
                >
                  <FontAwesomeIcon icon={['far', 'clock']} fixedWidth />
                  アラート通知を受け取るタイミング
                </div>
                <div
                  style={{
                    padding: "8px",
                    backgroundColor: "#F7F7F7",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "baseline",
                    gap: "2px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                    }}
                  >
                    {TIMING_TYPE.find((v) => v.value === customAlert.send_timing)?.label}
                    {customAlert.send_timing !== TIMING_VALUES.IMMEDIATE && (
                      `${customAlert.send_time}`
                    )}
                  </div>
                  <div>
                    にアラートが通知されます
                  </div>
                </div>
              </div>
              <div
                style={{
                  padding: "8px",
                }}
              >
                <div
                  style={{
                    padding: "0 8px",
                    marginBottom: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: customAlert.active ? "#a50404" : "#999999",
                  }}
                >
                  <FontAwesomeIcon icon={['far', 'eye']} fixedWidth />
                  アラート対象範囲
                </div>
                <div
                  style={{
                    padding: "12px 8px 8px",
                    backgroundColor: customAlert.active ? "#ffeded" : "#F7F7F7",
                    border: "solid 1px #CCCCCC",
                    borderRadius: "4px",
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  {
                    !!customAlert.subject.teams.length && (
                      <div
                        style={{
                          position: "relative",
                          display: "flex",
                          flexFlow: "column",
                          gap: "4px",
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          padding: "8px",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            transform: "translateY(-50%)",
                            padding: "0 4px",
                            fontSize: "10px",
                            backgroundColor: customAlert.active ? "#ffeded" : "#F7F7F7",
                          }}
                        >
                          アラート対象チーム
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "4px",
                            maxHeight: "250px",
                            overflowY: "scroll",
                          }}
                        >
                          {
                            customAlert.subject.teams.map((v) => (
                              <div
                                key={`${customAlert.id}_subject_team_${v.id}`}
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                }}
                              >
                                <div
                                  style={{
                                    backgroundColor: "#EEEEEE",
                                    padding: "4px 8px",
                                    borderRadius: "4px 4px 0 0",
                                    fontSize: "10px",
                                  }}
                                >
                                  {`${v.name}の${SCOPE_LABEL[v.scope]}${v.include_nested_teams ? " + 配下チーム" : ""}`}
                                </div>
                                <div
                                  style={{
                                    backgroundColor: "#FFFFFF",
                                    padding: "12px 8px 8px",
                                    borderRadius: "0 0 4px 4px",
                                    display: "flex",
                                    flexFlow: "column",
                                    gap: "8px",
                                  }}
                                >
                                  {
                                    ["both", "manager"].includes(v.scope) && (
                                      <div
                                        style={{
                                          position: "relative",
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "4px",
                                          padding: "8px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: 0,
                                            transform: "translateY(-50%)",
                                            padding: "0 4px",
                                            fontSize: "10px",
                                            backgroundColor: "#FFFFFF",
                                          }}
                                        >
                                          マネージャー
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          {v.managers.map((w) => buildMemberChip(w))}
                                        </div>
                                      </div>
                                    )
                                  }
                                  {
                                    ["both", "member"].includes(v.scope) && !v.include_nested_teams && (
                                      <div
                                        style={{
                                          position: "relative",
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "4px",
                                          padding: "8px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: 0,
                                            transform: "translateY(-50%)",
                                            padding: "0 4px",
                                            fontSize: "10px",
                                            backgroundColor: "#FFFFFF",
                                          }}
                                        >
                                          このチームのメンバー
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          {v.members.map((w) => buildMemberChip(w))}
                                        </div>
                                      </div>
                                    )
                                  }
                                  {
                                    ["both", "member"].includes(v.scope) && v.include_nested_teams && (
                                      <div
                                        style={{
                                          position: "relative",
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "4px",
                                          padding: "8px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: 0,
                                            transform: "translateY(-50%)",
                                            padding: "0 4px",
                                            fontSize: "10px",
                                            backgroundColor: "#FFFFFF",
                                          }}
                                        >
                                          このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          {
                                            Array.from(new Map([
                                              ...getAllMemberList(v.id || null),
                                              ...teamList.find((w) => w.id === v.id)?.members || [],
                                            ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((w) => (
                                              buildMemberChip(w)
                                            ))
                                          }
                                        </div>
                                      </div>
                                    )
                                  }
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    )
                  }
                  {
                    !!customAlert.subject.members.length && (
                      <div
                        style={{
                          position: "relative",
                          display: "flex",
                          flexFlow: "column",
                          gap: "4px",
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          padding: "8px 4px 4px",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            transform: "translateY(-50%)",
                            padding: "0 4px",
                            fontSize: "10px",
                            backgroundColor: customAlert.active ? "#ffeded" : "#F7F7F7",
                          }}
                        >
                          アラート対象ユーザー
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "4px",
                            maxHeight: "250px",
                            overflowY: "scroll",
                          }}
                        >
                          {
                            customAlert.subject.members.map((v) => buildMemberChip(v))
                          }
                        </div>
                      </div>
                    )
                  }
                  {
                    customAlert.subject.notify_self && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          borderRadius: "4px",
                          padding: "8px 16px",
                          backgroundColor: customAlert.active ? "#d60f00" : "#d7c5c4",
                          color: "#FFFFFF",
                        }}
                      >
                        <FontAwesomeIcon icon={['fas', 'dove']} />
                        <div>
                          条件に該当したら、アラート対象本人にも通知する設定になっています
                        </div>
                      </div>
                    )
                  }
                  {
                    !customAlert.subject.teams.length && !customAlert.subject.members.length && (
                      <div>
                        アラート対象範囲が設定されていません
                      </div>
                    )
                  }
                </div>
              </div>
              <div
                style={{
                  padding: "8px",
                }}
              >
                <div
                  style={{
                    padding: "0 8px",
                    marginBottom: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: customAlert.active ? "#00954e" : "#999999",
                  }}
                >
                  <FontAwesomeIcon icon={['far', 'paper-plane']} fixedWidth />
                  {`アラート通知を受け取るユーザー${customAlert.recipient.slack_channel ? "・Slackチャンネル" : ""}`}
                </div>
                <div
                  style={{
                    padding: "12px 8px 8px",
                    backgroundColor: customAlert.active ? "#e6fff3" : "#F7F7F7",
                    border: "solid 1px #CCCCCC",
                    borderRadius: "4px",
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  {
                    !!customAlert.recipient.members.length && (
                      <div
                        style={{
                          position: "relative",
                          display: "flex",
                          flexFlow: "column",
                          gap: "4px",
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          padding: "8px 4px 4px",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            transform: "translateY(-50%)",
                            padding: "0 4px",
                            fontSize: "10px",
                            backgroundColor: customAlert.active ? "#e6fff3" : "#F7F7F7",
                          }}
                        >
                          アラート通知を受け取るユーザー
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "4px",
                            maxHeight: "250px",
                            overflowY: "scroll",
                          }}
                        >
                          {customAlert.recipient.members.map((v) => buildMemberChip(v))}
                        </div>
                      </div>
                    )
                  }
                  {
                    !!customAlert.recipient.slack_channel && (
                      <div
                        style={{
                          position: "relative",
                          display: "flex",
                          flexFlow: "column",
                          gap: "4px",
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          padding: "8px 4px 4px",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            transform: "translateY(-50%)",
                            padding: "0 4px",
                            fontSize: "10px",
                            backgroundColor: customAlert.active ? "#e6fff3" : "#F7F7F7",
                          }}
                        >
                          通知するSlackチャンネル
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            padding: "8px",
                            backgroundColor: "#FFFFFF",
                          }}
                        >
                          {
                            customAlert.recipient.slack_channel.is_private ? (
                              <FontAwesomeIcon icon={['fas', 'lock']} />
                            ) : (
                              <FontAwesomeIcon icon={['fas', 'hashtag']} />
                            )
                          }
                          <span>
                            {customAlert.recipient.slack_channel.name}
                          </span>
                        </div>
                      </div>
                    )
                  }
                  {
                    !customAlert.recipient.members.length && !customAlert.recipient.slack_channel && (
                      <div>
                        アラート通知対象が設定されていません
                      </div>
                    )
                  }
                </div>
              </div>
              <div
                style={{
                  padding: "8px",
                }}
              >
                <div
                  style={{
                    padding: "0 8px",
                    marginBottom: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: customAlert.active ? "#0077a7" : "#999999",
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'list']} fixedWidth />
                  アラート条件
                </div>
                <div
                  style={{
                    padding: "8px",
                    backgroundColor: customAlert.active ? "#eaf9ff" : "#F7F7F7",
                    border: "solid 1px #CCCCCC",
                    borderRadius: "4px",
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  {
                    customAlert.conditions.map((condition) => (
                      <div
                        key={`${customAlert.id}_${condition.condition_type}`}
                        className="conditions"
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: "8px",
                          borderBottom: "solid 1px #CCCCCC",
                          padding: "2px 8px",
                        }}
                      >
                        <div
                          className="condition_name"
                          style={{
                            display: "flex",
                            gap: "4px",
                            minWidth: "300px",
                          }}
                        >
                          <div>
                            {[...CONDITION_TYPE, ...ATTENDANCE_CONDITION_TYPE].find((v) => v.value === condition.condition_type)?.label}
                          </div>
                          {
                            (
                              condition.condition_type === "application_time"
                              || condition.condition_type === "application_biz_point"
                            ) && (
                              <div className="target">
                                {`【${condition.service?.name}】`}
                              </div>
                            )
                          }
                        </div>
                        <div className="condition_detail">
                          {buildAlertCondition(condition)}
                          {
                            (condition.condition_type === "start_time" || condition.condition_type === "end_time") ? (
                              <>
                                {OPERATOR_TYPE.find((v) => v.value === condition.operator_type)?.label_1}
                              </>
                            ) : (
                              <>
                                {OPERATOR_TYPE.find((v) => v.value === condition.operator_type)?.label_2}
                              </>
                            )
                          }
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </>
          )
        }
      </div>
    </>
  )
}

export default CustomAlertDetail
